// ==================================================================================
// Digital Approval System (DAS) - Approver Endpoints
// ==================================================================================
// URL builders for approver lookup API endpoints
// ==================================================================================

import { buildEndpoint, buildEndpointWithQuery } from '../base.endpoints';
import type { ID } from '../../types/common.types';
import type { ApproverQueryParameters, ApproverType } from '../../types/das/approvers.types';

const BASE_PATH = 'das/approvers';

/**
 * Approver API Endpoints
 */
export const approverEndpoints = {
  /**
   * Get all approvers (users and roles combined)
   */
  getAll: (params?: ApproverQueryParameters) =>
    params && Object.keys(params).length > 0
      ? buildEndpointWithQuery(BASE_PATH, params)
      : buildEndpoint(BASE_PATH),

  /**
   * Get approvers with pagination
   */
  getPaged: (params: ApproverQueryParameters) =>
    buildEndpointWithQuery(`${BASE_PATH}/paged`, params),

  /**
   * Get user approvers only
   */
  getUsers: (params?: ApproverQueryParameters) =>
    params && Object.keys(params).length > 0
      ? buildEndpointWithQuery(`${BASE_PATH}/users`, params)
      : buildEndpoint(`${BASE_PATH}/users`),

  /**
   * Get role approvers only
   */
  getRoles: (params?: ApproverQueryParameters) =>
    params && Object.keys(params).length > 0
      ? buildEndpointWithQuery(`${BASE_PATH}/roles`, params)
      : buildEndpoint(`${BASE_PATH}/roles`),

  /**
   * Search approvers
   */
  search: (query: string, type?: ApproverType, limit?: number) => {
    const params: Record<string, string | number> = { q: query };
    if (type) params.type = type;
    if (limit) params.limit = limit;
    return buildEndpointWithQuery(`${BASE_PATH}/search`, params);
  },

  /**
   * Get user approver by ID
   */
  getUserById: (id: ID) => buildEndpoint(`${BASE_PATH}/users/${id}`),

  /**
   * Get role by ID
   */
  getRoleById: (id: ID) => buildEndpoint(`${BASE_PATH}/roles/${id}`),

  /**
   * Get users by role ID
   */
  getUsersByRole: (roleId: ID) => buildEndpoint(`${BASE_PATH}/roles/${roleId}/users`),

  /**
   * Get users by department ID
   */
  getUsersByDepartment: (departmentId: ID) =>
    buildEndpoint(`${BASE_PATH}/departments/${departmentId}/users`),

  /**
   * Get all roles list
   */
  getAllRoles: (activeOnly?: boolean) =>
    activeOnly !== undefined
      ? buildEndpointWithQuery(`${BASE_PATH}/roles/list`, { activeOnly })
      : buildEndpoint(`${BASE_PATH}/roles/list`),

  /**
   * Get all departments list
   */
  getAllDepartments: (activeOnly?: boolean) =>
    activeOnly !== undefined
      ? buildEndpointWithQuery(`${BASE_PATH}/departments/list`, { activeOnly })
      : buildEndpoint(`${BASE_PATH}/departments/list`),
};
